import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { School } from 'models/school';
import { SchoolEvent } from 'models/event';
import { Tournament } from 'models/tournament';
import { Sport } from 'models/sport';
import { CLUB_GENDER_SERVER_TO_CLIENT_MAPPING } from 'consts/club';
import { LIMIT_ALL } from 'consts/table';
import {
   getClubEventDatesForMonthFilter,
   getClubEventsForDayFilter,
   getEventDatesForMonthFilter,
   getEventsForDayFilter,
   getFixtureEventDatesForMonthFilter,
   getFixtureEventsForDayFilter,
   getTournamentDatesForMonthFilter,
   getTournamentEventDatesForMonthFilter,
   getTournamentEventsForDayFilter,
   getTournamentsForDayFilter,
   getEventResultForCalendar,
} from 'helpers/calendar';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getEventGeneratedName, isEventStatusCanceled, isEventStatusFinished } from 'helpers/event';
import { getSchoolEventLocation } from 'helpers/eventView';
import {
   getSchoolEventDates,
   getSchoolEvents,
   getSchoolSports,
   getSchoolTournamentDates,
   getSchoolTournaments,
} from 'services/school';
import Loader from 'components/loader';
import './Calendar.scss';

interface Props { school: School; }

type CalendarCategory = 'all' | 'fixtures' | 'clubs' | 'tournamentEvents' | 'tournaments';
type DateGroups = Record<Exclude<CalendarCategory, 'all'>, string[]>;

interface CalendarFilters {
   category: CalendarCategory;
   sport: string;
   age: string;
   gender: string;
}

const EMPTY_DATES: DateGroups = { fixtures: [], clubs: [], tournamentEvents: [], tournaments: [] };
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CATEGORY_LABELS: Record<CalendarCategory, string> = {
   all: 'All activity', fixtures: 'Fixtures', clubs: 'Clubs', tournamentEvents: 'Tournament events', tournaments: 'Tournaments',
};

const dateKey = (date: Date) => {
   const year = date.getFullYear();
   const month = String(date.getMonth() + 1).padStart(2, '0');
   const day = String(date.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
};

const parseDateParam = (value: string | null, fallback: Date) => {
   if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
   const date = new Date(`${value}T12:00:00`);
   return Number.isNaN(date.getTime()) ? fallback : date;
};

const dateFromApi = (value: string) => {
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? value.slice(0, 10) : dateKey(date);
};

const monthCells = (month: Date) => {
   const first = new Date(month.getFullYear(), month.getMonth(), 1);
   const mondayOffset = (first.getDay() + 6) % 7;
   const start = new Date(first);
   start.setDate(first.getDate() - mondayOffset);
   return Array.from({ length: 42 }, (_, index) => {
      const value = new Date(start);
      value.setDate(start.getDate() + index);
      return value;
   });
};

const formatHeadingDate = (date: Date) => new Intl.DateTimeFormat('en-GB', {
   weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
}).format(date);

const formatTime = (value: string) => new Intl.DateTimeFormat('en-GB', {
   hour: '2-digit', minute: '2-digit',
}).format(new Date(value));

const normaliseDates = (value: any): string[] => Array.isArray(value?.dates) ? value.dates.map(dateFromApi) : [];

export default function Calendar({ school }: Props) {
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const today = useMemo(() => new Date(), []);
   const selectedDate = parseDateParam(searchParams.get('day'), today);
   const monthParam = searchParams.get('month');
   const parsedMonth = monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? new Date(`${monthParam}-01T12:00:00`) : selectedDate;
   const month = Number.isNaN(parsedMonth.getTime()) ? selectedDate : parsedMonth;
   const filters: CalendarFilters = {
      category: (searchParams.get('category') as CalendarCategory) || 'all',
      sport: searchParams.get('sport') || '',
      age: searchParams.get('age') || '',
      gender: searchParams.get('gender') || '',
   };
   const [dates, setDates] = useState<DateGroups>(EMPTY_DATES);
   const [fixtureEvents, setFixtureEvents] = useState<SchoolEvent[]>([]);
   const [clubEvents, setClubEvents] = useState<SchoolEvent[]>([]);
   const [tournamentEvents, setTournamentEvents] = useState<SchoolEvent[]>([]);
   const [tournaments, setTournaments] = useState<Tournament[]>([]);
   const [sports, setSports] = useState<Sport[]>([]);
   const [isMonthLoading, setIsMonthLoading] = useState(true);
   const [isDayLoading, setIsDayLoading] = useState(true);
   const [monthError, setMonthError] = useState(false);
   const [dayError, setDayError] = useState(false);

   const categoryEnabled = (category: Exclude<CalendarCategory, 'all'>) => filters.category === 'all' || filters.category === category;
   const eventWhere: any = {};
   if (filters.sport) eventWhere.sportId = { $in: [filters.sport] };
   if (filters.age) eventWhere.ages = { $in: [Number(filters.age)] };
   if (filters.gender) eventWhere.gender = filters.gender;
   const tournamentWhere = { ...eventWhere };

   useEffect(() => {
      let active = true;
      getSchoolSports(school).then(result => { if (active) setSports(Array.isArray(result) ? result : []); }).catch(() => undefined);
      return () => { active = false; };
   }, [school.id]);

   useEffect(() => {
      let active = true;
      setIsMonthLoading(true);
      setMonthError(false);
      const baseEventFilter = getEventDatesForMonthFilter(month, school.id, eventWhere);
      const baseTournamentFilter = getTournamentDatesForMonthFilter(month);
      baseTournamentFilter.where = { ...baseTournamentFilter.where, ...tournamentWhere };
      const empty = Promise.resolve({ dates: [] });

      Promise.all([
         categoryEnabled('fixtures') ? getSchoolEventDates(school.id, getFixtureEventDatesForMonthFilter(baseEventFilter)) : empty,
         categoryEnabled('clubs') ? getSchoolEventDates(school.id, getClubEventDatesForMonthFilter(baseEventFilter)) : empty,
         categoryEnabled('tournamentEvents') ? getSchoolEventDates(school.id, getTournamentEventDatesForMonthFilter(baseEventFilter)) : empty,
         categoryEnabled('tournaments') ? getSchoolTournamentDates(school.id, baseTournamentFilter) : empty,
      ])
         .then(([fixtures, clubs, tournamentEventsResult, tournamentResult]) => {
            if (!active) return;
            setDates({
               fixtures: normaliseDates(fixtures),
               clubs: normaliseDates(clubs),
               tournamentEvents: normaliseDates(tournamentEventsResult),
               tournaments: normaliseDates(tournamentResult),
            });
         })
         .catch(() => { if (active) { setDates(EMPTY_DATES); setMonthError(true); } })
         .finally(() => { if (active) setIsMonthLoading(false); });
      return () => { active = false; };
   }, [school.id, month.getFullYear(), month.getMonth(), filters.category, filters.sport, filters.age, filters.gender]);

   useEffect(() => {
      let active = true;
      setIsDayLoading(true);
      setDayError(false);
      const baseEventFilter = getEventsForDayFilter(selectedDate, school.id, eventWhere);
      const baseTournamentFilter = getTournamentsForDayFilter(selectedDate);
      baseTournamentFilter.where = { ...baseTournamentFilter.where, ...tournamentWhere };
      const empty = Promise.resolve([]);

      Promise.all([
         categoryEnabled('fixtures') ? getSchoolEvents(school.id, getFixtureEventsForDayFilter(baseEventFilter)) : empty,
         categoryEnabled('clubs') ? getSchoolEvents(school.id, getClubEventsForDayFilter(baseEventFilter)) : empty,
         categoryEnabled('tournamentEvents') ? getSchoolEvents(school.id, getTournamentEventsForDayFilter(baseEventFilter)) : empty,
         categoryEnabled('tournaments') ? getSchoolTournaments(school.id, baseTournamentFilter) : empty,
      ])
         .then(([fixtures, clubs, tournamentEventItems, tournamentItems]) => {
            if (!active) return;
            setFixtureEvents(Array.isArray(fixtures) ? fixtures : []);
            setClubEvents(Array.isArray(clubs) ? clubs : []);
            setTournamentEvents(Array.isArray(tournamentEventItems) ? tournamentEventItems : []);
            setTournaments(Array.isArray(tournamentItems) ? tournamentItems : []);
         })
         .catch(() => {
            if (active) {
               setFixtureEvents([]); setClubEvents([]); setTournamentEvents([]); setTournaments([]); setDayError(true);
            }
         })
         .finally(() => { if (active) setIsDayLoading(false); });
      return () => { active = false; };
   }, [school.id, dateKey(selectedDate), filters.category, filters.sport, filters.age, filters.gender]);

   const updateParams = (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
      setSearchParams(next, { replace: true });
   };

   const chooseDay = (date: Date) => updateParams({ day: dateKey(date), month: dateKey(date).slice(0, 7) });
   const changeMonth = (offset: number) => {
      const next = new Date(month.getFullYear(), month.getMonth() + offset, 1);
      updateParams({ month: dateKey(next).slice(0, 7), day: dateKey(next) });
   };
   const goToday = () => updateParams({ day: dateKey(today), month: dateKey(today).slice(0, 7) });
   const clearFilters = () => updateParams({ category: null, sport: null, age: null, gender: null });

   const cells = monthCells(month);
   const selectedKey = dateKey(selectedDate);
   const todayKey = dateKey(today);
   const dateSets = {
      fixtures: new Set(dates.fixtures), clubs: new Set(dates.clubs),
      tournamentEvents: new Set(dates.tournamentEvents), tournaments: new Set(dates.tournaments),
   };
   const allDates = [...new Set(Object.values(dates).flat())].sort();
   const nextEventDate = allDates.find(value => value > selectedKey);
   const dayItems = [
      ...fixtureEvents.map(event => ({ kind: 'fixtures' as const, event })),
      ...clubEvents.map(event => ({ kind: 'clubs' as const, event })),
      ...tournamentEvents.map(event => ({ kind: 'tournamentEvents' as const, event })),
   ].sort((a, b) => new Date(a.event.startTime).getTime() - new Date(b.event.startTime).getTime());
   const sortedTournaments = [...tournaments].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
   const activeFilters = [filters.category !== 'all' && ['category', CATEGORY_LABELS[filters.category]], filters.sport && ['sport', sports.find(item => item.id === filters.sport)?.name || filters.sport], filters.age && ['age', getAgesGroupFormatted({ ages: [Number(filters.age)] }, school)], filters.gender && ['gender', CLUB_GENDER_SERVER_TO_CLIENT_MAPPING[filters.gender as keyof typeof CLUB_GENDER_SERVER_TO_CLIENT_MAPPING] || filters.gender]].filter(Boolean) as string[][];
   const availableAges = [...new Set(school.availableAges || [])].sort((a, b) => a - b);
   const hasMonthActivity = allDates.length > 0;
   const hasDayActivity = dayItems.length + sortedTournaments.length > 0;

   return (
      <main className='school-calendar'>
         <header className='school-calendar__heading'>
            <div><h1>Calendar</h1><p>Fixtures, clubs and tournaments in one place.</p></div>
            <Link to='/subscribe'><NotificationsNoneRoundedIcon /> Subscribe</Link>
         </header>

         <section className='school-calendar__filters' aria-label='Filter calendar'>
            <label><span>Show</span><select value={filters.category} onChange={event => updateParams({ category: event.target.value === 'all' ? null : event.target.value })}>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label><span>Sport</span><select value={filters.sport} onChange={event => updateParams({ sport: event.target.value || null })}><option value=''>All sports</option>{sports.map(sport => <option key={sport.id} value={sport.id}>{sport.name}</option>)}</select></label>
            <label><span>Year group</span><select value={filters.age} onChange={event => updateParams({ age: event.target.value || null })}><option value=''>All years</option>{availableAges.map(age => <option key={age} value={age}>{getAgesGroupFormatted({ ages: [age] }, school)}</option>)}</select></label>
            <label><span>Gender</span><select value={filters.gender} onChange={event => updateParams({ gender: event.target.value || null })}><option value=''>All</option><option value='MALE_ONLY'>Boys</option><option value='FEMALE_ONLY'>Girls</option><option value='MIXED'>Mixed</option></select></label>
            {activeFilters.length > 0 && <div className='school-calendar__filter-chips'>{activeFilters.map(([key, label]) => <button key={key} type='button' onClick={() => updateParams({ [key]: null })}>{label}<CloseRoundedIcon /></button>)}<button className='is-clear' type='button' onClick={clearFilters}>Clear all</button></div>}
         </section>

         <div className='school-calendar__layout'>
            <section className='school-calendar__month'>
               <div className='school-calendar__month-heading'>
                  <button type='button' onClick={() => changeMonth(-1)} aria-label='Previous month'><ChevronLeftRoundedIcon /></button>
                  <h2>{new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(month)}</h2>
                  <button type='button' onClick={() => changeMonth(1)} aria-label='Next month'><ChevronRightRoundedIcon /></button>
                  <button className='is-today' type='button' onClick={goToday}>Today</button>
               </div>
               <div className='school-calendar__weekdays' aria-hidden='true'>{WEEK_DAYS.map(day => <span key={day}>{day}</span>)}</div>
               <div className={`school-calendar__grid ${isMonthLoading ? 'is-loading' : ''}`}>
                  {cells.map(date => {
                     const key = dateKey(date);
                     const isOutside = date.getMonth() !== month.getMonth();
                     return <button key={key} type='button' className={`${isOutside ? 'is-outside' : ''} ${key === selectedKey ? 'is-selected' : ''} ${key === todayKey ? 'is-today' : ''}`} onClick={() => chooseDay(date)} aria-label={formatHeadingDate(date)} aria-pressed={key === selectedKey}>
                        <span>{date.getDate()}</span>
                        <i className='school-calendar__dots'>
                           {dateSets.fixtures.has(key) && <b className='is-fixture' title='Fixture' />}
                           {dateSets.clubs.has(key) && <b className='is-club' title='Club' />}
                           {dateSets.tournamentEvents.has(key) && <b className='is-tournament-event' title='Tournament event' />}
                           {dateSets.tournaments.has(key) && <b className='is-tournament' title='Tournament' />}
                        </i>
                     </button>;
                  })}
                  {isMonthLoading && <div className='school-calendar__month-loader'><Loader /></div>}
               </div>
               <div className='school-calendar__legend'><span><i className='is-fixture' />Fixture</span><span><i className='is-club' />Club</span><span><i className='is-tournament-event' />Tournament event</span><span><i className='is-tournament' />Tournament</span></div>
               {monthError && <p className='school-calendar__month-error'><ErrorOutlineRoundedIcon /> Calendar dates could not be loaded. Please refresh the page.</p>}
            </section>

            <section className='school-calendar__day'>
               <div className='school-calendar__day-heading'><div><span>Selected day</span><h2>{formatHeadingDate(selectedDate)}</h2></div>{hasDayActivity && <strong>{dayItems.length + sortedTournaments.length} {dayItems.length + sortedTournaments.length === 1 ? 'item' : 'items'}</strong>}</div>
               {isDayLoading ? <div className='school-calendar__day-loader'><Loader /></div> : dayError ? <div className='school-calendar__empty' role='alert'><ErrorOutlineRoundedIcon /><h3>Events could not be loaded</h3><p>Please refresh the page and try again.</p></div> : hasDayActivity ? <div className='school-calendar__items'>
                  {dayItems.map(({ kind, event }) => {
                     const title = getEventGeneratedName(event, school.id) || event.sport?.name || 'School event';
                     const location = getSchoolEventLocation(event, school.id, school.postcode?.id || '');
                     const result = isEventStatusFinished(event) || isEventStatusCanceled(event) ? getEventResultForCalendar(event, school.id) : '';
                     return <button key={`${kind}-${event.id}`} type='button' className='school-calendar__event' onClick={() => navigate(`/event/${event.id}`, { state: { selectedDate, prevPath: '/calendar' } })}>
                        <span className='school-calendar__time'>{formatTime(event.startTime)}</span>
                        <span className='school-calendar__event-copy'><span className={`school-calendar__kind is-${kind}`}>{CATEGORY_LABELS[kind]}</span><strong>{title}</strong><small>{event.sport?.name || 'School sport'} · {getAgesGroupFormatted({ ages: [...(event.ages || [])] }, school)} · {location}</small>{result && <em>{result}</em>}</span>
                        <ArrowForwardRoundedIcon />
                     </button>;
                  })}
                  {sortedTournaments.map(tournament => <button key={`tournament-${tournament.id}`} type='button' className='school-calendar__event' onClick={() => navigate(`/tournament/${tournament.id}`)}>
                     <span className='school-calendar__time'>{formatTime(tournament.startTime)}</span>
                     <span className='school-calendar__event-copy'><span className='school-calendar__kind is-tournaments'>Tournament</span><strong>{tournament.name}</strong><small>{tournament.sport?.name || 'School sport'}{tournament.isAgeGroupsOnWebSiteVisible && tournament.ages?.length ? ` · ${getAgesGroupFormatted({ ages: [...tournament.ages] }, school)}` : ''}</small></span>
                     <ArrowForwardRoundedIcon />
                  </button>)}
               </div> : <div className='school-calendar__empty'><CalendarMonthRoundedIcon /><h3>{hasMonthActivity ? 'No events on this day' : activeFilters.length ? 'Nothing matches these filters' : 'Nothing scheduled this month'}</h3><p>{hasMonthActivity ? 'Choose a day marked with a coloured dot to see what is happening.' : activeFilters.length ? 'Try another combination or clear the filters to see more activity.' : 'New fixtures, clubs and tournaments will appear here when the school publishes them.'}</p><div>{nextEventDate && <button type='button' onClick={() => chooseDay(new Date(`${nextEventDate}T12:00:00`))}>Next event day <ArrowForwardRoundedIcon /></button>}{activeFilters.length > 0 && <button type='button' className='is-secondary' onClick={clearFilters}>Clear filters</button>}{!nextEventDate && !activeFilters.length && <Link to='/subscribe'><NotificationsNoneRoundedIcon /> Subscribe</Link>}</div></div>}
               <div className='school-calendar__day-nav'><button type='button' onClick={() => { const previous = new Date(selectedDate); previous.setDate(previous.getDate() - 1); chooseDay(previous); }}><ArrowBackRoundedIcon /> Previous day</button><button type='button' onClick={() => { const next = new Date(selectedDate); next.setDate(next.getDate() + 1); chooseDay(next); }}>Next day <ArrowForwardRoundedIcon /></button></div>
            </section>
         </div>
      </main>
   );
}
